import type { components } from "@majitap/sdk";
import { Button, PageError, PageHeader, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import Switch from "@majitap/ui/arc/switch/switch";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { messageFor } from "../../error-copy";
import { allStaffQuery } from "../../team";
import styles from "./settings.module.css";

type AlertRow = components["schemas"]["AlertList"]["data"][number];
type Channel = components["schemas"]["AlertRuleBody"]["channels"][number];

const alertsQuery = queryOptions({
  queryKey: ["alerts"],
  queryFn: async () => (await ok(api.GET("/v1/admin/alerts"))).data,
});

const GROUPS: { id: AlertRow["group"]; label: string }[] = [
  { id: "orders", label: "Orders" },
  { id: "deliveries", label: "Deliveries" },
  { id: "payments", label: "Payments" },
  { id: "ops", label: "Ops issues" },
];
const CHANNELS: { id: Channel; label: string; help: string }[] = [
  { id: "sms", label: "SMS", help: "To each person's phone, and to the extra numbers" },
  { id: "email", label: "Email", help: "To each person's sign-in email" },
  { id: "console", label: "Console", help: "In the bell, and as a notification in their browser if they turned it on" },
];
const BUILT_IN: Record<string, string> = {
  "deliveries.dispatch": "dispatchers",
  "customers.manage": "staff who manage customers",
  "payouts.approve": "staff who approve payouts",
  "integrations.manage": "staff who manage integrations",
};

/** Who an alert reaches, in a line: "Mary, John and 1 number · SMS, Email". */
function summary(rule: NonNullable<AlertRow["rule"]>) {
  const who = [
    ...rule.staff.map((s) => s.name),
    ...(rule.phones.length ? [`${rule.phones.length} ${rule.phones.length === 1 ? "number" : "numbers"}`] : []),
  ];
  const by = rule.channels.map((c) => CHANNELS.find((x) => x.id === c)?.label ?? c).join(", ");
  return `${who.join(", ") || "Nobody"} · ${by}`;
}

/** Alerts (P11-12, Settings › Team): who hears about which events, and how. */
export function AlertsPage(): ReactNode {
  const alerts = useQuery(alertsQuery);
  const [editing, setEditing] = useState<AlertRow | null>(null);
  const header = (
    <PageHeader
      title="Alerts"
      description="Choose who hears about what happens, and how: by SMS, email or in the console."
    />
  );
  if (alerts.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading the alerts" />
      </>
    );
  if (alerts.status === "error")
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(alerts.error))} onRetry={() => alerts.refetch()} />
      </>
    );
  return (
    <>
      {header}
      {GROUPS.map((group) => (
        <section key={group.id} className={styles.card} aria-label={group.label}>
          <h2 className={styles.sectionTitle}>{group.label}</h2>
          <ul className={styles.list}>
            {alerts.data
              .filter((a) => a.group === group.id)
              .map((a) => (
                <li key={a.alert} className={styles.row}>
                  <span className={styles.stack}>
                    <span>
                      {a.label} {a.rule && !a.rule.enabled ? <Badge tone="neutral">Off</Badge> : null}
                    </span>
                    <span className={styles.muted}>
                      {a.rule
                        ? summary(a.rule)
                        : a.built_in
                          ? `Only the ${BUILT_IN[a.built_in] ?? "usual staff"}, in the console`
                          : "Nobody"}
                    </span>
                  </span>
                  <TextButton onClick={() => setEditing(a)}>{a.rule ? "Edit" : "Set up"}</TextButton>
                </li>
              ))}
          </ul>
        </section>
      ))}
      {editing ? <AlertDialog alert={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function AlertDialog({ alert, onClose }: { alert: AlertRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const staff = useQuery(allStaffQuery);
  const rule = alert.rule;
  const [channels, setChannels] = useState<Channel[]>(rule?.channels ?? ["console"]);
  const [people, setPeople] = useState<string[]>(rule?.staff.map((s) => s.user_id) ?? []);
  const [phones, setPhones] = useState(rule?.phones.join(", ") ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [problem, setProblem] = useState<string | null>(null);
  const done = async (data: components["schemas"]["AlertList"]) => {
    queryClient.setQueryData(alertsQuery.queryKey, data.data);
    onClose();
  };
  const save = useMutation({
    mutationFn: (body: components["schemas"]["AlertRuleBody"]) =>
      ok(api.PUT("/v1/admin/alerts/{alert}", { params: { path: { alert: alert.alert } }, body })),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => ok(api.DELETE("/v1/admin/alerts/{alert}", { params: { path: { alert: alert.alert } } })),
    onSuccess: done,
  });
  const active = (staff.data ?? []).filter((s) => s.status === "active");
  const chosen = active.filter((s) => people.includes(s.user.id));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const numbers = phones
      .split(/[,\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const bad = numbers.find((p) => !/^\+[1-9]\d{8,14}$/.test(p));
    const next = !channels.length
      ? "Choose at least one way to send it."
      : !people.length && !numbers.length
        ? "Choose someone to alert, or add a phone number."
        : bad
          ? `${bad} isn't a phone number in international format, e.g. +254712345678.`
          : null;
    setProblem(next);
    if (!next) save.mutate({ channels, staff_user_ids: people, phones: numbers, enabled });
  };
  const toggle = <T,>(list: T[], item: T, on: boolean) => (on ? [...list, item] : list.filter((x) => x !== item));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={alert.label}
        description={
          alert.built_in
            ? `The ${BUILT_IN[alert.built_in] ?? "usual staff"} already get this in the console. Choose who else hears, and how.`
            : "Choose who hears about it, and how."
        }
      >
        <form onSubmit={submit} className={styles.form}>
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Send by</legend>
            {CHANNELS.map((c) => (
              <Checkbox
                key={c.id}
                label={c.label}
                description={c.help}
                checked={channels.includes(c.id)}
                onCheckedChange={(on) => setChannels(toggle(channels, c.id, on === true))}
              />
            ))}
          </fieldset>
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Staff</legend>
            {staff.status === "pending" ? <p className={styles.muted}>Loading the staff…</p> : null}
            {active.map((s) => (
              <Checkbox
                key={s.user.id}
                label={s.user.name}
                description={s.roles.map((r) => r.name).join(", ")}
                checked={people.includes(s.user.id)}
                onCheckedChange={(on) => setPeople(toggle(people, s.user.id, on === true))}
              />
            ))}
          </fieldset>
          {channels.includes("sms") && chosen.some((s) => !s.user.phone_number) ? (
            <p className={styles.muted}>
              No phone for{" "}
              {chosen
                .filter((s) => !s.user.phone_number)
                .map((s) => s.user.name)
                .join(", ")}
              : they get no SMS.
            </p>
          ) : null}
          {channels.includes("email") && chosen.some((s) => !s.user.email) ? (
            <p className={styles.muted}>
              No email for{" "}
              {chosen
                .filter((s) => !s.user.email)
                .map((s) => s.user.name)
                .join(", ")}
              : they get no email.
            </p>
          ) : null}
          <Input
            label="Extra phone numbers (optional)"
            description="Numbers that aren't staff, such as an operations line, separated by commas. They get the SMS."
            placeholder="+254712345678"
            value={phones}
            onChange={(e) => setPhones(e.target.value)}
          />
          {phones.trim() && !channels.includes("sms") ? (
            <p className={styles.muted}>Extra numbers only get SMS: tick SMS to send to them.</p>
          ) : null}
          <Switch label="On" checked={enabled} onCheckedChange={setEnabled} />
          {problem ? <p className={styles.danger}>{problem}</p> : null}
          {save.error || remove.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error ?? remove.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            {rule ? (
              <Button type="button" variant="secondary" loading={remove.isPending} onClick={() => remove.mutate()}>
                Stop this alert
              </Button>
            ) : null}
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
